import React, { useEffect, useCallback, useRef} from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { unstable_usePrompt, useLocation, useNavigate } from 'react-router-dom';

import { RealexPaymentFrame, LoadingIndicator } from '@digital/react-aviva-atom';
import { setProgressStep } from '../../redux/progress-bar/progressBar';
import { ProvisionalBookingInfo } from '../../components/provisional-bookinginfo/ProvisionalBookingInfo';
import { ImportantInformation } from '../../components/important-information/ImportantInformation';
import {
    setPaymentResponse,
    setPaymentError,
    getPayment,
    RealexPaymentSuccessResponse,
    RealexPaymentErrorResponse,
} from '../../redux/payment/payment';

import { createBooking } from '../../redux/booking/booking';
import { appRoutesByName } from '../app/Routes';
import { createSelector } from 'reselect';
import { RootState } from '../../app/store';

export const selectPaymentData = createSelector(
    (state: RootState) => state.userSelection.lessonTime,
    (state: RootState) => state.userSelection.productId,
    (state: RootState) => state.products.availableProducts,
    (state: RootState) => state.payment.paymentRequest,
    (state: RootState) => state.payment.paymentRequestLoading,
    (state: RootState) => state.booking.bookingProcessing,
    (lessonTime, productId, availableProducts, paymentRequest, paymentRequestLoading, bookingProcessing) => ({
        lessonTime,
        productDescription: productId ? availableProducts[productId]?.description : undefined,
        productPrice: productId ? availableProducts[productId]?.price : undefined,
        paymentRequestConfig: paymentRequest,
        paymentRequestLoading,
        bookingProcessing,
    })
);

const Payment: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const {
        lessonTime,
        productDescription,
        productPrice,
        paymentRequestConfig,
        paymentRequestLoading,
        bookingProcessing,
    } = useAppSelector(selectPaymentData);

    // Block navigating elsewhere when data has been entered into the input
    unstable_usePrompt({
        message: "WARNING! Payment processing, Are you sure?",
        when: ({ currentLocation, nextLocation }) =>
        bookingProcessing &&
            currentLocation.pathname !== appRoutesByName.payment.path &&
            nextLocation.pathname !== appRoutesByName.confirmation.path,
    });


    const VITE_DS_PAYMENT_ACTION = import.meta.env.VITE_DS_PAYMENT_ACTION;
    const VITE_DS_PAYMENT_ORIGIN = import.meta.env.VITE_DS_PAYMENT_ORIGIN;
    const VITE_DS_PAYMENT_RESPONSE = import.meta.env.VITE_DS_PAYMENT_RESPONSE;

    useEffect(() => {
        if (!VITE_DS_PAYMENT_ACTION || !VITE_DS_PAYMENT_ORIGIN || !VITE_DS_PAYMENT_RESPONSE) {
            throw new Error('ERROR: Payment environment variables are not set');
        }
    }, [VITE_DS_PAYMENT_ACTION, VITE_DS_PAYMENT_ORIGIN, VITE_DS_PAYMENT_RESPONSE]);

    useEffect(() => {
        dispatch(setProgressStep({ step: 4 }));
        const appEventData = 'AppEventData';
        (window as any)[appEventData]?.push({
            event: 'stepLoaded',
            page: {
                pageName: 'ie:secure:my-aviva:driving-school:payment',
                channel: 'secure',
            },
        });
    }, [dispatch]);

    const handlePaymentError = useCallback(
        (error: RealexPaymentErrorResponse) => {
            dispatch(setPaymentError({ paymentError: error }));
            switch (error.resultCode[0]) {
                case '1':
                    return dispatch(getPayment());
                default:
                    navigate(appRoutesByName.errorPage.path);
                    return Promise.reject(error);
            }
        },
        [dispatch, navigate]
    );

    const handlePaymentSuccess = useCallback(
        (response: RealexPaymentSuccessResponse) => {

            dispatch(setPaymentResponse({ paymentResponse: response }));
            return dispatch(createBooking({navigate}));
        },
        [dispatch]
    );

    const loading = paymentRequestLoading || bookingProcessing;
    const iframeDisplay = loading ? 'none' : 'block';
    const realexHppRequestConfig = paymentRequestConfig as { [key: string]: string } | undefined;

    return (
        <div className="payment-container" data-testid="payment-container">
            <h1 className="text__center">Payment</h1>
            {lessonTime && <ProvisionalBookingInfo userSelection={lessonTime} />}
            <h3>What you will pay today</h3>
            <hr className="a-keyline-divider--horizontal-solid-light" />
            <div className="m-form-row">
                You are purchasing the following package:
                <p className="a-heading a-heading--3 a-heading--light">{productDescription}</p>
                <p className="a-heading a-heading--3 a-heading--semibold">€{productPrice}</p>
            </div>
            <h3>Enter your payment details</h3>
            <hr className="a-keyline-divider--horizontal-solid-light" />
            {loading && (
                <div className="a-loading-indicator-container centre" data-testid="loading-indicator">
                    <LoadingIndicator size="large" />
                    <p className="loading-message">
                        {paymentRequestLoading ? 'Loading...' : 'Processing...'}
                    </p>
                </div>
            )}
            <div style={{ display: iframeDisplay }}>
                <RealexPaymentFrame
                    onPaymentError={handlePaymentError}
                    onPaymentSuccess={handlePaymentSuccess}
                    paymentActionURL={VITE_DS_PAYMENT_ACTION}
                    paymentOriginURL={VITE_DS_PAYMENT_ORIGIN}
                    paymentResponseURL={VITE_DS_PAYMENT_RESPONSE}
                    realexHppRequestConfig={ realexHppRequestConfig }
                />
            </div>
            <div className="m-form-row">
                <ImportantInformation />
            </div>
        </div>
    );
};

export default Payment;
